"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Calculator, Scale, Stethoscope, Wrench, Briefcase, ShoppingBag, Cpu, Building2, ArrowRight, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const sectorsData = [{
  title: "Accountants & Financial Services",
  description: "High-intent local and national search strategy targeting high-net-worth clients, corporate inquiries, and specialist tax planning.",
  iconName: "Calculator",
  tag: "Finance & Legal"
}, {
  title: "Solicitors & Legal Practices",
  description: "Bespoke SEO for law firms focusing on competitive legal keywords, geo-targeted practice area pages, and high-trust client acquisition.",
  iconName: "Scale",
  tag: "Legal Services"
}, {
  title: "Dentists & Dental Clinics",
  description: "Dominating local map packs and organic listings for high-value treatments, private dental procedures, and emergency appointments.",
  iconName: "Stethoscope",
  tag: "Healthcare"
}, {
  title: "Plumbers, Electricians & Trades",
  description: "Hyper-focused local search visibility ensuring immediate search presence for domestic repairs, commercial work, and urgent callouts.",
  iconName: "Wrench",
  tag: "Trades & Field Services"
}, {
  title: "B2B & Corporate Services",
  description: "Capturing key decision-makers through long-tail intent keywords, authoritative technical foundations, and pipeline-driving content strategy.",
  iconName: "Briefcase",
  tag: "B2B Lead Gen"
}, {
  title: "Ecommerce & Retail Brands",
  description: "Structured schema markup, faceted navigation cleanup, category page authority, and product-level optimization to scale revenue.",
  iconName: "ShoppingBag",
  tag: "Online Retail"
}, {
  title: "SaaS & Tech Companies",
  description: "Product-led search strategy, high-conversion comparison pages, and scalable technical architecture for sustainable international growth.",
  iconName: "Cpu",
  tag: "Technology"
}, {
  title: "Property & Real Estate Agencies",
  description: "Geo-targeted listing visibility, local market authority building, and high-converting valuation inquiry lead generation.",
  iconName: "Building2",
  tag: "Real Estate"
}];
function getSectorIcon(name: string) {
  const iconProps = {
    className: "size-6 text-primary shrink-0"
  };
  switch (name) {
    case "Calculator":
      return <Calculator {...iconProps} />;
    case "Scale":
      return <Scale {...iconProps} />;
    case "Stethoscope":
      return <Stethoscope {...iconProps} />;
    case "Wrench":
      return <Wrench {...iconProps} />;
    case "Briefcase":
      return <Briefcase {...iconProps} />;
    case "ShoppingBag":
      return <ShoppingBag {...iconProps} />;
    case "Cpu":
      return <Cpu {...iconProps} />;
    case "Building2":
      return <Building2 {...iconProps} />;
    default:
      return <Briefcase {...iconProps} />;
  }
}
export default function Sectors() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="industrysectorsgrid" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-40 bg-[radial-gradient(ellipse_at_top,_var(--color-secondary)_0%,_transparent_60%)]" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold tracking-wide uppercase bg-secondary text-secondary-foreground border border-border">
            <Sparkles className="size-3.5 text-primary" /> Industry Tailored SEO
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-default">
            Specialised SEO Strategies for Your Industry Sector
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Every sector has unique search behaviors, compliance considerations, and customer journeys. I build customized search growth strategies tailored directly to your specific market.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {sectorsData.map((sector, i) => <motion.div key={sector.title} variants={itemVariants} data-index={i} className="h-full">
              <Card className="h-full flex flex-col justify-between transition-[border-color,box-shadow] duration-300 hover:border-primary/50 hover:shadow-md bg-card border-border">
                <CardHeader className="space-y-3 pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex size-12 items-center justify-center rounded-lg bg-primary/10 border border-primary/20">
                      {getSectorIcon(sector.iconName)}
                    </div>
                    <Badge variant="outline" className="text-[13px] font-medium text-muted-foreground border-border">
                      {sector.tag}
                    </Badge>
                  </div>
                  <CardTitle className="text-lg font-semibold text-card-foreground pt-1 line-clamp-1">
                    {sector.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 flex-1 flex flex-col justify-between">
                  <CardDescription className="text-sm text-muted-foreground leading-relaxed mb-4">
                    {sector.description}
                  </CardDescription>
                  <div className="pt-2 border-t border-border/60 flex items-center text-xs font-medium text-primary">
                    <CheckCircle2 className="size-3.5 mr-1.5 shrink-0 text-primary" />
                    <span>Direct consultant implementation</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="mt-16 rounded-2xl bg-muted/60 border border-border p-6 sm:p-8 lg:p-10 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-sm">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="text-xl sm:text-2xl font-semibold text-foreground">
              Don&apos;t see your specific industry listed?
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground">
              I work directly with ambitious UK and international businesses across a wide range of niche sectors.
            </p>
          </div>
          <div className="shrink-0 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Button size="lg" asChild className="w-full sm:w-auto">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button variant="outline" size="lg" asChild className="w-full sm:w-auto">
              <Link to="/CaseStudiesPage">
                Explore Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}